import { type ReactElement, useEffect, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { SubTableModel, usePromise } from '@cancercentrum/rcc-react';
import { StandardTabHeader } from '../../components/StandardTabHeader.tsx';
import HTab from '../../../components/HTabs/HTab.tsx';
import { ModuleContext } from '../../ModuleContext.ts';
import { useIPO } from '../../hooks/useIPO.ts';
import type { KontaktRowModel, KontaktSubTable } from '../../../offline/muu2.0/form-oversikt/brostcancer/form.brostcancer.muu';
import { DisplayName, GridId, ModuleName, TableName } from './constants.ts';
import { useGetKontaktSubTable } from './useGetKontaktSubTable.ts';
import { LatestPalsContext } from './LatestPalsContext.ts';
import { getPreviousKontaktRegistration } from './kontaktUtils.ts';

const EMPTY_ARRAY: string[] = [];

const KontaktTab = (props: {
    highlight?: boolean;
    children: ReactElement;
}): ReactElement => {
    const { children, highlight } = props;
    const ipo = useIPO();
    const palsPromise = usePromise<string[]>();
    const ctx = useMemo(() => {
        return {
            moduleName: ModuleName,
            displayName: DisplayName,
            tableName: TableName,
            gridId: GridId,
        };
    }, []);
    const subTable = useGetKontaktSubTable();
    const subTableRef = useRef<KontaktSubTable>(subTable);
    subTableRef.current = subTable;

    const header = (
        <StandardTabHeader
            displayName={DisplayName}
            rows={subTable.items}
            className={classnames({
                'tab-highlight': highlight,
            })}
        />
    );
    useEffect(() => {
        const handler = (row: KontaktRowModel) => {
            if (row.$rcc.tableName !== TableName) {
                return row;
            }

            const latest = getPreviousKontaktRegistration(subTableRef.current.getRows().toArray());
            if (!latest) {
                return row;
            }

            let res = row;

            // Om diagnosen har kontaktsjuksköterska.
            if (ipo.dataset[TableName]?.K_kontaktsjukskoterska) {
                res = res.setRegvarValue('K_kontaktsjukskoterska', latest.getRegvarValue('K_kontaktsjukskoterska')?.id || null);
            }

            // Om diagnosen har anslutits till palliativt team ska de förifyllas.
            if (ipo.dataset[TableName]?.K_palliativtTeam) {
                res = res.setRegvarValue('K_palliativtTeam', latest.getRegvar('K_palliativtTeam').value || null);
            }

            // Om diagnosen har TTF/orsak/svar så ska de förifyllas
            res = ([
                'K_erbjuditsTTF2',
                'K_erbjuditsTTF_orsak',
                'K_erbjuditsTTF_orsak_spec',
                'K_erbjuditsTTF_svar',
                'K_erbjuditsTTF_svar_spec',
            ] as const).reduce((acc, shortname) => {
                if (ipo.dataset[TableName]?.[shortname]) {
                    return acc.setRegvarValue(shortname, latest.getRegvar(shortname).value || null);
                }

                return acc;
            }, res);

            // Om diagnosen har palliativt team så ska de förifyllas
            res = ([
                'K_palliativtTeam',
                'K_datumPalliativtTeam',
                'K_inskrivenPalliativHemsjukvard',
            ] as const).reduce((acc, shortname) => {
                if (ipo.dataset[TableName]?.[shortname]) {
                    return acc.setRegvarValue(shortname, latest.getRegvar(shortname).value || null);
                }

                return acc;
            }, res);

            return res;
        };

        // console.log('[dbg] adding row handler for kontakt');
        SubTableModel.hooks.createRow.add(handler);

        return () => {
            // console.log('[dbg] removing row handler for kontakt');
            SubTableModel.hooks.createRow.remove(handler);
        };
    }, [subTableRef, ipo]);

    return (
        <HTab eventKey={ModuleName} header={header}>
            <LatestPalsContext.Provider value={palsPromise.result || EMPTY_ARRAY}>
                <ModuleContext.Provider value={ctx}>
                    <div data-testid={`Module_${ModuleName}`}>
                        {children}
                    </div>
                </ModuleContext.Provider>
            </LatestPalsContext.Provider>
        </HTab>
    );
};

KontaktTab.ModuleName = ModuleName;

export default KontaktTab;
